import type {
  ChapterAPIList,
  ChapterList,
  CourseAPIList,
  CourseList,
  LimitOffsetSort,
  ListMeta,
} from '@jojotique/server'
import { generateCard } from '@comp/template/Card/generate.ts'
import { checkDifficultyValue, checkSortValue, deleteQueryParam, getImgUrl, hasQueryParam } from '@core/utils/url.ts'
import { generatePagination } from '@core/components/template/pagination/generate.ts'
import { loadChapters } from '@service/chapters.ts'
import { loadCourses } from '@service/courses.ts'
import type { SnackbarElement } from '@comp/customHtml/Snackbar'

export type ElementPaginationType = 'tutorials' | 'courses'

interface PaginationOptions {
  difficulty?: string
  isComponents?: boolean
  pageNumber: string
  sort?: string
}

class ChaptersCoursesPaginationPage {
  updateSelectValues(sortSelect: HTMLSelectElement, difficultySelect: HTMLSelectElement) {
    const queries = new URLSearchParams(window.location.search)
    if (queries.has('sort') && checkSortValue(queries.get('sort'))) {
      sortSelect.value = queries.get('sort')!
    }
    if (queries.has('difficulty') && checkDifficultyValue(queries.get('difficulty'))) {
      difficultySelect.value = queries.get('difficulty')!
    } else {
      difficultySelect.value = 'all'
    }
  }

  loadElements(type: ElementPaginationType, { pageNumber, sort, difficulty, isComponents }: PaginationOptions) {
    const options: LimitOffsetSort = {
      page: pageNumber,
    }

    if (!isComponents) {
      options.limit = '6'
      options.sort = checkSortValue(sort) ? sort : 'newest'
      options.difficulty = checkDifficultyValue(difficulty) ? difficulty : 'all'
    }

    ;(type === 'tutorials' ? loadChapters(options, { isComponents }) : loadCourses(options))
      .then(this.#responseTreatment.bind(this, type))
      .catch((error: string) => {
        document.querySelector<SnackbarElement>('app-snackbar')?.show(error, 'Error')
      })
  }

  /**
   * Retourne le texte du bouton en fonction de l'index fourni.
   * @param {number} i - L'index de l'élément (cours ou chapitre).
   * @returns {string} Le texte du bouton correspondant.
   */
  #getButtonText(i: number): string {
    switch (i) {
      case 0:
        return 'Lancer la simulation'
      case 1:
        return "Commencer l'aventure"
      case 2:
        return 'Sécuriser mes connaissances'
      case 3:
        return 'Explorer ce domaine'
      case 4:
        return 'Déverrouiller le savoir'
      case 5:
      default:
        return 'Voir la mission'
    }
  }

  /**
   * Réinitialise la liste des éléments affichés sur la page (cours ou chapitres).
   * Vide le contenu de la balise <ul> principale.
   * @returns {{ chaptersCoursesContainer: HTMLUListElement }} L'élément <ul> principal vidé.
   */
  #resetChaptersCourses(): { chaptersCoursesContainer: HTMLUListElement } {
    const chaptersCoursesContainer = document.querySelector<HTMLUListElement>('main > ul')!
    chaptersCoursesContainer.innerHTML = ''
    return { chaptersCoursesContainer }
  }

  /**
   * Initialise l'affichage des cours ou chapitres sur la page.
   * Crée dynamiquement les éléments HTML pour chaque item fourni.
   * @param {ElementPaginationType} type - Le type d'éléments à afficher (cours ou chapitres).
   * @param {(CourseList|ChapterList)[]} courses - La liste des cours ou chapitres à afficher.
   */
  #initChaptersCourses(type: ElementPaginationType, courses: Array<CourseList | ChapterList>) {
    const { chaptersCoursesContainer } = this.#resetChaptersCourses()

    courses.forEach((course, i) => {
      const listItem = document.createElement('li')

      const courseLink = document.createElement('a')
      courseLink.href = `/${type === 'courses' ? 'course' : 'chapter'}/${course.slug}`
      courseLink.title = `Naviguer vers le cours "${course.title}"`

      courseLink.appendChild(
        listItem.appendChild(
          generateCard(
            document,
            {
              abstract: course.abstract ?? 'Aucune description disponible.',
              buttonText: this.#getButtonText(i),
              id: course.id,
              src: course.thumbnail?.name ? getImgUrl(course.thumbnail!.name) : '',
              title: course.title,
            },
            {
              type: i % 2 === 0 ? 'secondary' : 'primary',
              hover: true,
            },
          ),
        ),
      )

      listItem.appendChild(courseLink)
      chaptersCoursesContainer?.appendChild(listItem)
    })
  }

  /**
   * Initialise la pagination de la liste des éléments (cours/chapitres).
   * Met à jour le composant de pagination avec les données fournies.
   * @param {Document} document - Le document HTML dans lequel la pagination sera générée.
   * @param {ListMeta} pagination - Les informations de pagination.
   */
  #initPagination(document: Document, pagination: ListMeta): void {
    generatePagination(document, pagination.currentPage, pagination.totalPages)
  }

  /**
   * Active ou désactive l'affichage du message "aucun cours" selon le statut fourni.
   * Utilisé pour indiquer l'absence de cours ou de chapitres.
   * @param {boolean} status - true pour afficher le message, false pour le masquer.
   */
  #toggleNoCoursesMessageStatus(status: boolean) {
    document.querySelector<HTMLUListElement>('main > section')?.classList.toggle('active', status)
  }

  /**
   * Traite la réponse de l'API pour une liste de cours ou de chapitres.
   * Comportement :
   * - Si la liste est vide : réinitialise la liste affichée, affiche le message "aucun cours/chapitre",
   *   initialise la pagination, et supprime le paramètre `page` de l'URL si nécessaire.
   * - Si la liste contient des éléments : masque le message "aucun cours/chapitre", initialise
   *   l'affichage des éléments et la pagination, et supprime aussi le paramètre `page` si la
   *   pagination ne comporte qu'une page.
   * Effets de bord : modifie le DOM (liste principale, message d'absence, pagination) et peut
   * supprimer le paramètre `page` de l'URL.
   * @param {ElementPaginationType} type - Le type d'éléments traités (cours ou chapitres).
   * @param {(ChapterAPIList|CourseAPIList)} response - La réponse de l'API contenant `data` et `meta`.
   */
  #responseTreatment(type: ElementPaginationType, response: ChapterAPIList | CourseAPIList) {
    if (response.data.length === 0) {
      this.#resetChaptersCourses()
      this.#toggleNoCoursesMessageStatus(true)
      this.#initPagination(document, response.meta)
      if (response.meta.totalPages === 1 && hasQueryParam('page')) deleteQueryParam('page')
      return
    }

    this.#toggleNoCoursesMessageStatus(false)
    this.#initChaptersCourses(type, response.data)
    this.#initPagination(document, response.meta)
    if (response.meta.totalPages === 1 && hasQueryParam('page')) deleteQueryParam('page')
  }
}

export const useChaptersCourses = new ChaptersCoursesPaginationPage()
